import React, { useState, useEffect } from 'react';
import Layout from '../../../app/Layout';
import Breadcrumb from '../General/Breadcrumb';
import HeaderContent from '../General/HeaderContent';
import SpinnerLoading from '../../components/Spinners/SpinnerLoading';
import DataTableComponent from "../../components/plugins/DataTableComponent";
import PieChart from './PieChart';
import {
    faClock, faHashtag, faListAlt, faEdit,
    faCheckCircle, faHourglassHalf, faExclamationTriangle,
    faPhone, faSearch, faSyncAlt, faCalendarCheck, faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import FormularioFiltroLlamadas from './FormularioFiltroLlamadas';
import FormularioFiltroLlamadasAsesor from './FormularioFiltroLlamadasAsesor';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from 'react-bootstrap';
import { Link } from "react-router-dom";

const KpiCard = ({ icon, label, value, sub, color }) => (
    <div className="card mb-3" style={{ borderLeft: `4px solid ${color}` }}>
        <div className="card-body py-3 px-3">
            <div className="d-flex justify-content-between align-items-center">
                <div>
                    <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
                    <div className="font-weight-bold" style={{ fontSize: '1.8rem', color, lineHeight: 1 }}>{value}</div>
                    {sub && <div className="mt-1" style={{ fontSize: '11px', color: '#999' }}>{sub}</div>}
                </div>
                <div style={{ width: 44, height: 44, borderRadius: '50%', backgroundColor: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FontAwesomeIcon icon={icon} style={{ color, fontSize: '1.2rem' }} />
                </div>
            </div>
        </div>
    </div>
);

const ReporteLlamadas = () => {
    const [loading, setLoading] = useState(false);
    const [datosTabla, setDatosTabla] = useState({ data: [], grafico: null });
    const [filtroTexto, setFiltroTexto] = useState('');
    const [filtroProposito, setFiltroProposito] = useState('');
    const { usuario } = useSelector(state => state.usuario);

    useEffect(() => {
        const cached = localStorage.getItem('reporte_llamadas_cache');
        if (cached) {
            try { setDatosTabla(JSON.parse(cached)); } catch (e) {}
        }
    }, []);

    const formatTiempo = (seconds) => {
        if (!seconds) return '00:00';
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.ceil(seconds % 60);
        return `${m < 10 ? '0' + m : m}:${s < 10 ? '0' + s : s}`;
    };

    const formatTiempoTotal = (seconds) => {
        if (!seconds) return '00:00:00';
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.ceil(seconds % 60);
        return `${h < 10 ? '0' + h : h}:${m < 10 ? '0' + m : m}:${s < 10 ? '0' + s : s}`;
    };

    const data = datosTabla.data || [];

    // KPIs del período
    const today = new Date().toISOString().split('T')[0];
    const completadas = data.filter(r => r.completado === '1').length;
    const pendientes  = data.filter(r => r.completado === '0').length;
    const vencidas    = data.filter(r => r.completado === '2').length;
    const ejecutadasHoy = data.filter(r => r.completado === '1' && (r.fecha_hora || '').startsWith(today)).length;
    const tiempoConversadoHoy = data
        .filter(r => r.completado === '1' && (r.fecha_hora || '').startsWith(today))
        .reduce((sum, r) => sum + (parseInt(r.tiempo_conversado) || 0), 0);

    // Panel derecho
    const tasaEjecucion = data.length > 0 ? ((completadas / data.length) * 100).toFixed(1) : '0.0';
    const asesoresUnicos = [...new Set(data.map(r => r.nombre_usuario).filter(Boolean))].length;
    const promedioPorLlamada = completadas > 0
        ? formatTiempoTotal(Math.floor((datosTabla.tiempo_conversado || 0) / completadas))
        : '00:00:00';
    const llamadasPorAsesor = asesoresUnicos > 0 ? (data.length / asesoresUnicos).toFixed(1) : '0';

    // Propósitos únicos para el filtro
    const propositosUnicos = [...new Set(data.map(r => r.nombre_proposito).filter(Boolean))];

    // Filtros cliente-side: propósito + texto
    const dataMostrada = data
        .filter(r => !filtroProposito || r.nombre_proposito === filtroProposito)
        .filter(r => {
            if (!filtroTexto) return true;
            const txt = filtroTexto.toLowerCase();
            return (
                `${r.nombres || ''} ${r.apellidos || ''}`.toLowerCase().includes(txt) ||
                (r.telefono || '').includes(filtroTexto) ||
                (r.nombre_proyecto || '').toLowerCase().includes(txt) ||
                (r.nombre_usuario || '').toLowerCase().includes(txt)
            );
        });

    const handleActualizar = () => setDatosTabla(prev => ({ ...prev }));

    return (
        <Layout>
            <div className="ControlReportes">
                <div className="ControlReportes__header">
                    <HeaderContent title='Reporte de Llamadas' />
                    <Breadcrumb link={[
                        { url: "/inicio", nombre: "Inicio", icon: "fas fa-home" },
                        { url: "", nombre: "Reportes" },
                        { url: "", nombre: "Llamadas" },
                    ]} />
                </div>
                <div className="ControlReportes__body">

                    {/* 1. KPI cards */}
                    <div className="row">
                        <div className="col-md col-6">
                            <KpiCard icon={faHourglassHalf} label="Pendientes" value={pendientes} sub="Por ejecutar" color="#f6c23e" />
                        </div>
                        <div className="col-md col-6">
                            <KpiCard icon={faExclamationTriangle} label="Vencidas" value={vencidas} sub="Atrasadas" color="#e74a3b" />
                        </div>
                        <div className="col-md col-6">
                            <KpiCard icon={faCalendarCheck} label="Ejecutadas hoy" value={ejecutadasHoy} sub="Completadas hoy" color="#1cc88a" />
                        </div>
                        <div className="col-md col-6">
                            <KpiCard icon={faClock} label="T. conversado hoy" value={formatTiempoTotal(tiempoConversadoHoy)} sub="Acumulado del día" color="#36b9cc" />
                        </div>
                        <div className="col-md col-6">
                            <KpiCard icon={faHashtag} label="Total seguimientos" value={data.length} sub="En el período" color="#4e73df" />
                        </div>
                    </div>

                    {/* 2. Banner */}
                    <div className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center justify-content-between" style={{ fontSize: '13px' }}>
                        <span>
                            <i className="fas fa-info-circle mr-2"></i>
                            Los seguimientos programados se ejecutan desde Detalle de Lead y su resultado se registra aquí automáticamente.
                        </span>
                        <Link to="/leads" className="btn btn-sm btn-outline-info ml-3" style={{ whiteSpace: 'nowrap' }}>
                            <i className="fas fa-external-link-alt mr-1"></i>Ver Detalle de Lead
                        </Link>
                    </div>

                    {/* 2. Filtros (server-side + client-side en un solo bloque) */}
                    <div className="card mb-3" style={{ border: '1px solid #dee2e6' }}>
                        <div className="card-header py-2 px-3" style={{ background: '#f8f9fc', borderBottom: '1px solid #dee2e6' }}>
                            <span className="font-weight-bold small" style={{ color: '#434343' }}>
                                <i className="fas fa-filter mr-2"></i>Filtros de búsqueda
                            </span>
                        </div>
                        <div className="card-body p-2">
                            {/* Fila 1: filtros server-side */}
                            {usuario.id_tp_nivel_usuario !== '11'
                                ? <FormularioFiltroLlamadas setDatosTabla={setDatosTabla} setLoading={setLoading} usuario={usuario} />
                                : <FormularioFiltroLlamadasAsesor setDatosTabla={setDatosTabla} setLoading={setLoading} usuario={usuario} />
                            }
                            {/* Fila 2: filtros client-side */}
                            <div className="row align-items-end mx-n1 mt-1">
                                <div className="col-md-3 px-1">
                                    <label className="small font-weight-bold mb-1">Propósito</label>
                                    <select className="form-control form-control-sm" value={filtroProposito} onChange={e => setFiltroProposito(e.target.value)}>
                                        <option value="">Todos</option>
                                        {propositosUnicos.map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-5 px-1">
                                    <label className="small font-weight-bold mb-1">Buscar</label>
                                    <div className="input-group input-group-sm">
                                        <div className="input-group-prepend">
                                            <span className="input-group-text"><FontAwesomeIcon icon={faSearch} /></span>
                                        </div>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Lead, teléfono, producto, asesor..."
                                            value={filtroTexto}
                                            onChange={e => setFiltroTexto(e.target.value)}
                                        />
                                        {filtroTexto && (
                                            <div className="input-group-append">
                                                <button className="btn btn-outline-secondary btn-sm" onClick={() => setFiltroTexto('')}>
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-4 px-1 d-flex align-items-end justify-content-end" style={{ gap: '6px' }}>
                                    <small className="text-muted">
                                        Mostrando <strong>{dataMostrada.length}</strong> de <strong>{data.length}</strong> registros
                                    </small>
                                    <button className="btn btn-outline-secondary btn-sm" onClick={handleActualizar} title="Actualizar tabla">
                                        <FontAwesomeIcon icon={faSyncAlt} className="mr-1" />Actualizar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 3. Tabla + 4. Panel lateral */}
                    <div className="row">
                        <div className="col-md-9">
                            <SpinnerLoading loading={loading}>
                                <div className="card">
                                    <div className="card-body">
                                        <h6 className="font-weight-bold mb-3" style={{ color: '#434343' }}>
                                            <FontAwesomeIcon icon={faListAlt} className="mr-2" />Lista de Seguimientos
                                        </h6>
                                        <DataTableComponent
                                            colums={colums(formatTiempo)}
                                            tabla={dataMostrada}
                                            keyField="id_llamada"
                                        />
                                    </div>
                                </div>
                            </SpinnerLoading>
                        </div>

                        {/* 4. Panel lateral */}
                        <div className="col-md-3">

                            {/* Gráfico donut */}
                            {datosTabla.grafico
                                ? <PieChart info={datosTabla.grafico} />
                                : (
                                    <div className="card mb-2">
                                        <div className="card-body text-center text-muted py-4">
                                            <FontAwesomeIcon icon={faListAlt} size="2x" className="mb-2" style={{ opacity: 0.3 }} /><br />
                                            <small>Sin datos del gráfico</small>
                                        </div>
                                    </div>
                                )
                            }

                            {/* Resumen del período */}
                            <div className="card mb-2">
                                <div className="card-body p-3">
                                    <h6 className="font-weight-bold mb-3" style={{ color: '#434343', fontSize: '13px' }}>
                                        <i className="fas fa-clipboard-list mr-2" style={{ color: '#4e73df' }}></i>Resumen del período
                                    </h6>
                                    {[
                                        { label: 'Total seguimientos', val: data.length, color: '#4e73df' },
                                        { label: 'Pendientes',         val: pendientes,  color: '#f6c23e' },
                                        { label: 'Vencidas',          val: vencidas,    color: '#e74a3b' },
                                        { label: 'Completadas',       val: completadas, color: '#1cc88a' },
                                    ].map((item, i) => (
                                        <div key={i} className="d-flex justify-content-between align-items-center mb-2">
                                            <div className="d-flex align-items-center">
                                                <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: item.color, marginRight: 8, flexShrink: 0 }} />
                                                <small style={{ color: '#555' }}>{item.label}</small>
                                            </div>
                                            <strong style={{ color: item.color }}>{item.val}</strong>
                                        </div>
                                    ))}
                                    <div className="d-flex justify-content-between align-items-center mt-2 pt-2" style={{ borderTop: '1px solid #f0f0f0' }}>
                                        <small className="text-muted">Tasa de ejecución</small>
                                        <span className="font-weight-bold" style={{ color: '#1cc88a', fontSize: '15px' }}>{tasaEjecucion}%</span>
                                    </div>
                                </div>
                            </div>

                            {/* Rendimiento del período */}
                            <div className="card">
                                <div className="card-body p-3">
                                    <h6 className="font-weight-bold mb-3" style={{ color: '#434343', fontSize: '13px' }}>
                                        <i className="fas fa-chart-line mr-2" style={{ color: '#36b9cc' }}></i>Rendimiento del período
                                    </h6>
                                    {[
                                        { icon: faClock,   label: 'T. total conversado', val: formatTiempoTotal(datosTabla.tiempo_conversado), color: '#36b9cc' },
                                        { icon: faPhone,   label: 'Promedio por llamada', val: promedioPorLlamada,  color: '#4e73df' },
                                        { icon: faHashtag, label: 'Llamadas por asesor',  val: llamadasPorAsesor,  color: '#1cc88a' },
                                    ].map((item, i) => (
                                        <div key={i} className="d-flex align-items-center mb-3">
                                            <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: item.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 10, flexShrink: 0 }}>
                                                <FontAwesomeIcon icon={item.icon} style={{ color: item.color, fontSize: '13px' }} />
                                            </div>
                                            <div>
                                                <div className="font-weight-bold" style={{ fontSize: '14px', lineHeight: 1.2 }}>{item.val}</div>
                                                <small className="text-muted">{item.label}</small>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                        </div>
                    </div>

                </div>
            </div>
        </Layout>
    );
};

export default ReporteLlamadas;

const colums = (formatTiempo) => [
    {
        name: "Estado",
        selector: "completado",
        sortable: true,
        width: "95px",
        cell: (record) => {
            const vencida = record.completado === '2';
            return (
                <Badge variant={record.completado === '1' ? 'success' : vencida ? 'danger' : 'warning'}>
                    {record.completado === '1' ? 'Completado' : vencida ? 'Vencida' : 'Pendiente'}
                </Badge>
            );
        }
    },
    {
        name: "Acción",
        selector: "destino",
        width: "145px",
        cell: (record) => {
            const esPendiente = record.completado !== '1';
            if (!esPendiente) {
                return (
                    <Link
                        className="btn btn-xs btn-outline-secondary"
                        to={`detalleRegistro/${record.destino}${record.id_llamada ? `?llamada=${record.id_llamada}` : ''}`}
                    >
                        <FontAwesomeIcon icon={faEdit} className="mr-1" />Ver detalle
                    </Link>
                );
            }
            return (
                <div className="d-flex" style={{ gap: '3px' }}>
                    <Link
                        className="btn btn-xs btn-primary"
                        to={`detalleRegistro/${record.destino}?llamada=${record.id_llamada}`}
                        title="Gestionar llamada"
                    >
                        <FontAwesomeIcon icon={faPhone} className="mr-1" />Gestionar
                    </Link>
                    <Link
                        className="btn btn-xs btn-outline-warning"
                        to={`detalleRegistro/${record.destino}?reprogramar=${record.id_llamada}`}
                        title="Reprogramar"
                    >
                        <FontAwesomeIcon icon={faCalendarAlt} />
                    </Link>
                </div>
            );
        }
    },
    {
        name: "Fecha",
        selector: "fecha_hora",
        sortable: true,
        wrap: true,
        width: "115px",
    },
    {
        name: "Lead",
        selector: "nombres",
        sortable: true,
        wrap: true,
        width: "120px",
        cell: (record) => <span>{record.nombres} {record.apellidos}</span>
    },
    {
        name: "Teléfono",
        selector: "telefono",
        wrap: true,
        width: "100px",
    },
    {
        name: "Producto",
        selector: "nombre_proyecto",
        sortable: true,
        wrap: true,
        width: "130px",
    },
    {
        name: "Asesor",
        selector: "nombre_usuario",
        sortable: true,
        wrap: true,
        width: "100px",
    },
    {
        name: "Propósito",
        selector: "nombre_proposito",
        sortable: true,
        wrap: true,
        width: "110px",
    },
    {
        name: "Resultado",
        selector: "tiempo_conversado",
        width: "95px",
        cell: (record) => {
            if (record.completado !== '1') return <span className="text-muted">—</span>;
            return parseInt(record.tiempo_conversado) > 0
                ? <Badge variant="success">Respondió</Badge>
                : <Badge variant="secondary">No atendió</Badge>;
        }
    },
    {
        name: "Duración",
        selector: "tiempo_llamada",
        sortable: true,
        width: "75px",
        cell: (record) => <span>{formatTiempo(record.tiempo_llamada)}</span>
    },
    {
        name: "Conversado",
        selector: "tiempo_conversado",
        sortable: true,
        width: "80px",
        cell: (record) => <span>{formatTiempo(record.tiempo_conversado)}</span>
    },
    {
        name: "Trato",
        selector: "convertido",
        width: "65px",
        cell: (record) => (record.convertido === '1' || record.convertido === 1)
            ? <Badge variant="info">Trato</Badge>
            : <Badge variant="secondary">—</Badge>
    },
];
